import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MUIStyledCommonProps } from "@mui/system";
import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { drawerWidth } from ".";
import { getSessionCookie, isLoggedIn } from "../components/cookies";
import { commonSlice, commonState, store } from "../store";
import { colors, fontSizes } from "../theme";

const DrawerHeader = styled("div")<MUIStyledCommonProps>(() => ({
    display: "flex",
    alignItems: "center",
    height: 80,
    padding: 0,
    justifyContent: "flex-end",
    borderBottom: "1px solid",
    borderColor: colors.gray.a20,
}));

type MenuItemProps = {
    to: string;
    children: React.ReactNode;
};
const MenuItem: React.FC<MenuItemProps> = ({ to, children }: MenuItemProps) => {
    const location = useLocation();
    return (
        <ListItem>
            <ListItemButton
                component={RouterLink}
                to={to}
                selected={location.pathname == to}
            >
                <ListItemText primary={children} />
            </ListItemButton>
        </ListItem>
    );
};

export const LeftNavMenu: React.FC<commonState> = (props: commonState) => {
    const permissions = getSessionCookie().consoleUserPermissions;
    const monitoringPermitted = permissions.includes("monitoring");
    const reservePermitted = permissions.includes("reserve");
    const chargeablePermitted = permissions.includes("chargeable");

    return (
        <Drawer
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: drawerWidth,
                    boxSizing: "border-box",
                },
                "& h4": {
                    pt: 6,
                    pl: 4,
                    color: colors.gray.main,
                    fontSize: fontSizes.small,
                    fontWeight: "bold",
                },
            }}
            variant="persistent"
            anchor="left"
            open={props.navigation.leftNavMenuIsCollapsed}
        >
            <DrawerHeader>
                <Typography
                    variant="h1"
                    sx={{
                        fontSize: fontSizes.xlarge,
                        color: colors.green.main,
                        lineHeight: "24px",
                        textDecoration: "none",
                        "&:hover": {
                            opacity: 0.7,
                        },
                    }}
                    component={RouterLink}
                    to={"/"}
                >
                    お客様探索ナビ
                    <br />
                    管理コンソール
                </Typography>
                <IconButton
                    onClick={() => {
                        store.dispatch(
                            commonSlice.actions.toggleLeftMenuCollapsedState()
                        );
                    }}
                    sx={{
                        m: 4,
                        p: 3,
                        "&:hover": {
                            backgroundColor: colors.gray.a10,
                        },
                    }}
                >
                    <ArrowBackIosOutlinedIcon />
                </IconButton>
            </DrawerHeader>
            <Box sx={{ overflowY: "auto" }}>
                {monitoringPermitted && (
                    <>
                        <List>
                            <MenuItem to="/weekly_report">
                                週次レポート
                            </MenuItem>
                            <MenuItem to="/shift_search">乗務検索</MenuItem>
                        </List>
                        <Divider />
                    </>
                )}
                {reservePermitted && (
                    <>
                        <List>
                            <MenuItem to="/reserve/shifts">
                                乗務レポート
                            </MenuItem>
                        </List>
                        <Divider />
                    </>
                )}
                {chargeablePermitted && (
                    <>
                        <Typography variant={"h4"}>従量制</Typography>
                        <List>
                            <MenuItem to="/payments/summary">
                                請求金額確認
                            </MenuItem>
                            <MenuItem to="/driver_usage">利用実績照会</MenuItem>
                            <MenuItem to="/capacity">課金上限設定</MenuItem>
                        </List>
                        <Divider />
                    </>
                )}
                {isLoggedIn() && !reservePermitted && (
                    <>
                        <List>
                            <MenuItem to="/drivers_list_menu">
                                契約乗務員一覧
                            </MenuItem>
                        </List>
                    </>
                )}
                {isLoggedIn() && (
                    <>
                        <Divider />
                        <List>
                            <MenuItem to="/release_note">
                                リリースノート
                            </MenuItem>
                            <MenuItem to="/manual">マニュアル</MenuItem>
                        </List>
                    </>
                )}
            </Box>
        </Drawer>
    );
};
